import { Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { TargetModule } from "@enums/document/TargetModule";
import { nameof } from "@methods/jeffs-toolkit";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { HyperlinkModel } from "@models/hyperlinks/hyperlinkmodel";
import { ToastrService } from "ngx-toastr";
import { of, firstValueFrom } from "rxjs";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";

@Component({
    selector: "hyperlink-manager",
    templateUrl: "./hyperlink-manager.component.html",
    styleUrls: ["./hyperlink-manager.component.scss"],
    imports: [GenericListManagerComponent],
    viewProviders: [
        provideTranslationScope(scope),
    ]
})
export class HyperlinkManagerComponent implements OnInit {
    private _standingDataDS = inject(StandingdataDataService);
    private _toastr = inject(ToastrService);
    private _translation = injectTranslation(scope);

    @Input() Hyperlinks: HyperlinkModel[] = [];
    @Input() TargetModule: TargetModule;

    @Input() Label: string;
    @Input() Readonly: boolean = false;

    @Output() HyperlinksChanged = new EventEmitter<HyperlinkModel[]>();

    listConfig: GenericListConfig;
    hyperlinkTypes: IdNameCombination[];

    async ngOnInit(): Promise<void> {
        // Every string of this component goes into the generic-list config, so it is read before any
        // pipe renders this scope. Wait for the scope first. The template guards on listConfig, so
        // the first pass renders nothing.
        await this._translation.ready();
        await this.getStandingData();
        await this.initListManager();
    }

    private async getStandingData(): Promise<void> {
        this.hyperlinkTypes = await firstValueFrom(
            this._standingDataDS.getHyperlinkTypes(this.TargetModule)
        );
    }

    private initListManager(): void {
        this.listConfig = <GenericListConfig>{
            name: this.Label ?? this._translation.t("label"),
            idProp: nameof<HyperlinkModel>((h) => h.id),
            isSortable: false,

            uiid: "hyperlink-manager",
            uiidAdd: "hyperlink-add",
            uiidDelete: "hyperlink-delete",

            allowAdd: !this.Readonly,
            allowEdit: !this.Readonly,
            allowDelete: !this.Readonly,

            fields: [
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.name),
                    prettyName: this._translation.t("nameColumn"),
                    fieldType: GenericListFieldType.Text,
                    required: true,
                    uiid: "hyperlink-name",
                },
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.hyperlink),
                    prettyName: this._translation.t("hyperlinkColumn"),
                    fieldType: GenericListFieldType.TextArea,
                    required: true,
                    uiid: "hyperlink-hyperlink",
                    customValidation: (row) => {
                        var value = row.hyperlink;
                        const whiteSpacePattern = /[\s]/i;
                        const containsWhiteSpace = whiteSpacePattern.test(value);
                        if (containsWhiteSpace) {
                            return {
                                makeRequired: false,
                                validation: this._translation.t("whitespaceValidation"),
                            };
                        }
                    },
                },
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.hyperlinkTypeId),
                    prettyName: this._translation.t("hyperlinkTypeColumn"),
                    fieldType: GenericListFieldType.SingleSelect,
                    required: false,
                    uiid: "hyperlink-type",
                    getDataMethod: () => {
                        return of(this.hyperlinkTypes);
                    },
                },
                {
                    fieldName: nameof<HyperlinkModel>((h) => h.description),
                    prettyName: this._translation.t("descriptionColumn"),
                    fieldType: GenericListFieldType.TextArea,
                    required: false,
                    uiid: "hyperlink-description",
                },
            ],

            data: this.Hyperlinks,
            dataChanged: (data: HyperlinkModel[]) => {
                this.Hyperlinks = data;
                this.HyperlinksChanged.emit(data);
            },

            overviewRowActionsName: this._translation.t("openActionsColumn"),
            overviewRowActions: [
                {
                    icon: "fas fa-external-link-alt",
                    tooltip: this._translation.t("openHyperlinkTooltip"),
                    clickEvent: (row: HyperlinkModel) => {
                        if (row.hyperlink.indexOf(":") == -1)
                            this._toastr.warning(this._translation.t("missingProtocolWarning"));
                        else window.open(row.hyperlink, "_blank");
                    },
                },
            ],
        };
    }
}
